import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

const platformLogos = {
  square: { src: '/assets/Square_Logo_2025_Black.png', alt: 'Square' },
};

const featureLabels = {
  inventory_sync: 'Inventory Sync',
  sales_import: 'Sales Import',
  webhooks: 'Real-time Updates',
};

const PlatformSelector = ({ open, onClose, platforms, onSelect, isConnecting }) => {
  return (
    <Dialog
      open={open}
      onClose={isConnecting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-label="Connect to a platform"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Connect to a Platform</DialogTitle>
      <DialogContent>
        {isConnecting && (
          <Backdrop
            open
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Connecting...
              </Typography>
            </Box>
          </Backdrop>
        )}

        {(!platforms || platforms.length === 0) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No integration platforms are currently available.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {platforms.map((platform) => (
              <Card
                key={platform.id}
                variant="outlined"
                sx={{
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardContent>
                  {platformLogos[platform.id] ? (
                    <Box
                      component="img"
                      src={platformLogos[platform.id].src}
                      alt={platformLogos[platform.id].alt}
                      sx={{ height: 32, display: 'block', mb: 1 }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {platform.name}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {platform.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {platform.features?.map((feature) => (
                      <Chip
                        key={feature}
                        label={featureLabels[feature] || feature}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={() => onSelect(platform.id)}
                    disabled={isConnecting}
                    sx={{ textTransform: 'none' }}
                    aria-label={`Connect to ${platform.name}`}
                  >
                    Connect to {platform.name}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlatformSelector;
